import React, { useEffect, useState } from "react";
import { Button } from "@heathmont/moon-core-tw";
import { getSwapProposals, respondToSwapProposal } from "../../../services/request.service";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface ShiftRef { day: number; month: number; year: number; shiftType: string; }
interface Proposal {
  _id: string;
  requester: { _id: string; firstName: string; lastName: string; role: string };
  colleague: { _id: string; firstName: string; lastName: string; role: string };
  requesterShift: ShiftRef;
  colleagueShift: ShiftRef;
  status: "pending" | "accepted" | "rejected";
  createdAt: string;
}

const fmtShift = (s: ShiftRef) =>
  `${moment({ year: s.year, month: s.month - 1, day: s.day }).format("DD MMM YYYY")} · ${s.shiftType}`;

const SwapProposals = ({ onUpdate }: { onUpdate?: () => void }) => {
  const { User } = useSelector((state: RootState) => state.auth);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res: any = await getSwapProposals();
    setLoading(false);
    if (res?.res?.data) setProposals(res.res.data);
  };

  useEffect(() => { load(); }, []);

  const respond = async (id: string, action: "accept" | "reject") => {
    setActing(id + action);
    await respondToSwapProposal(id, action);
    setActing(null);
    load();
    onUpdate?.();
  };

  const myId = User?.id;
  // Only show incoming pending proposals here (outgoing appear in the table below)
  const pending = proposals.filter((p) => p.status === "pending" && p.colleague._id === myId);

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <div className="mb-6">
      {pending.length > 0 && (
        <>
          <h3 className="text-moon-16 font-semibold text-bulma mb-3">Incoming Swap Requests</h3>
          <p className="text-moon-12 text-trunks mb-2">A colleague has requested to swap shifts with you</p>
          <div className="space-y-3 mb-6">
            {pending.map((p) => {
              const iAmColleague = p.colleague._id === myId;
              const fromName = `${p.requester.firstName} ${p.requester.lastName}`;
              const theirShift = p.requesterShift;
              const myShift = p.colleagueShift;

              if (!iAmColleague) {
                // I'm the requester — show pending status
                return (
                  <div key={p._id} className="bg-white rounded-[8px] p-4 border border-beerus">
                    <p className="text-moon-14 font-semibold text-bulma">Awaiting response from {p.colleague.firstName} {p.colleague.lastName}</p>
                    <p className="text-moon-12 text-trunks mt-1">
                      Your shift: <span className="text-bulma font-medium">{fmtShift(theirShift)}</span>
                      {" · "}Their shift: <span className="text-bulma font-medium">{fmtShift(myShift)}</span>
                    </p>
                  </div>
                );
              }

              // I'm the colleague — show accept/reject
              return (
                <div key={p._id} className="bg-white rounded-[8px] p-4 border-l-4 border-hit shadow-sm">
                  <p className="text-moon-14 font-semibold text-bulma">
                    {fromName} wants to swap shifts with you
                  </p>
                  <div className="mt-2 text-moon-12 text-trunks space-y-1">
                    <p>They give you: <span className="text-bulma font-medium">{fmtShift(theirShift)}</span></p>
                    <p>You give them: <span className="text-bulma font-medium">{fmtShift(myShift)}</span></p>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      className="bg-hit"
                      disabled={!!acting}
                      onClick={() => respond(p._id, "accept")}
                    >
                      {acting === p._id + "accept" ? "Accepting…" : "Accept"}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!!acting}
                      onClick={() => respond(p._id, "reject")}
                    >
                      {acting === p._id + "reject" ? "Declining…" : "Decline"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

    </div>
  );
};

export default SwapProposals;
